import {
  Camera,
  Color,
  ColorRepresentation,
  Euler,
  Matrix4,
  Object3D,
  Quaternion,
  Vector3,
  WebGLRenderer,
} from 'three';
import { Text } from 'troika-three-text';

import { Cluster } from '../elements/cluster';
import { SCREEN_HEIGHT_PX } from '../elements/components/height-labels';
import { TextBatch } from '../elements/components/text-batch';
import { ElementObj } from '../elements/element-obj';
import { Geometry, geometryStyle } from '../elements/geometry';
import { Poi } from '../elements/poi/poi';
import {
  GeometryLayer,
  HexColor,
  Position3,
  VectorLayerContent,
  VectorPoi,
  VectorsLayer,
  VectorText,
} from './manifest';

export type VectorLayerOptions = {
  /** Whether a point in local coordinates is in the report's view. */
  isInView: (point: Vector3) => boolean;
  /** A perspective view through the camera, where texts face it: their rotation is on screen. */
  perspective: boolean;
  origo: Vector3;
  /** The paper resolution, for the size of texts sized on screen. */
  dpi: number;
  blackLinesAndText: boolean;
  /** To update the texts as before a render, so they're placed and sized as shown. */
  renderer?: WebGLRenderer;
  camera?: Camera;
};

export type VectorLayers = {
  layers: (GeometryLayer | VectorsLayer)[];
  /** The content of the vectors layers' files, by their path in the zip. */
  files: Record<string, VectorLayerContent>;
  /** The layer each object that draws them belongs to, to find them in the render lists. */
  layerIds: Map<Object3D, string>;
  /** Vectors layers that three.js doesn't draw, as the POIs are HTML: painted on top. */
  paintedLast: string[];
};

const BLACK: HexColor = '#000000';
const MM_PER_INCH = 25.4;

const toHex = (color: ColorRepresentation): HexColor => `#${new Color(color).getHexString()}`;

/** The paper height of a text of this many pixels on screen, rounded to a tenth of a millimeter. */
export const toPaperHeightMm = (pixels: number, dpi: number) =>
  Math.round(((pixels * MM_PER_INCH) / dpi) * 10) / 10;

function toAnchorX(anchor: Text['anchorX']): VectorText['anchorX'] {
  switch (anchor) {
    case 'center':
      return 'middle';
    case 'right':
      return 'right';
    // Numbers and percentages move the text a little from its position, as the height labels.
    default:
      return 'left';
  }
}

function toAnchorY(anchor: Text['anchorY']): VectorText['anchorY'] {
  switch (anchor) {
    case 'top':
    case 'top-cap':
    case 'top-ex':
      return 'top';
    case 'middle':
      return 'middle';
    case 'bottom-baseline':
      return 'baseline';
    default:
      return 'bottom';
  }
}

/**
 * The angle on screen of the text's x axis, counter-clockwise from the screen's x axis: its angle
 * in the camera's plane, which SkyView turns its labels in.
 */
function getScreenRotation(matrixWorld: Matrix4, camera: Camera | undefined) {
  if (!camera) {
    return 0;
  }

  const toCamera = camera.getWorldQuaternion(new Quaternion()).invert();
  const xAxis = new Vector3().setFromMatrixColumn(matrixWorld, 0).applyQuaternion(toCamera);
  return Math.atan2(xAxis.y, xAxis.x);
}

/**
 * The texts of the labels of an asset, in world coordinates, as they're shown: the batches of
 * height and length labels, and single texts. A perspective view has those in it, turned as on
 * screen.
 */
function collectTexts(labels: Object3D, options: VectorLayerOptions): VectorText[] {
  const texts: VectorText[] = [];
  const position = new Vector3();
  const quaternion = new Quaternion();
  const scale = new Vector3();

  const addText = (text: Text, matrixWorld: Matrix4) => {
    const value = Array.isArray(text.text) ? text.text.join('\n') : text.text;
    if (!value?.trim()) {
      return;
    }

    matrixWorld.decompose(position, quaternion, scale);
    if (options.perspective && !options.isInView(position)) {
      return;
    }
    const screenHeightPx = text.userData[SCREEN_HEIGHT_PX] as number | undefined;
    const outlined = typeof text.outlineWidth === 'number' && text.outlineWidth > 0;

    texts.push({
      value,
      position: position.clone().add(options.origo).toArray() as Position3,
      rotation: options.perspective
        ? getScreenRotation(matrixWorld, options.camera)
        : new Euler().setFromQuaternion(quaternion, 'ZYX').z,
      ...(screenHeightPx !== undefined
        ? { paperHeightMm: toPaperHeightMm(screenHeightPx, options.dpi) }
        : { height: text.fontSize * scale.y }),
      color: options.blackLinesAndText ? BLACK : toHex(text.color ?? 0xffffff),
      anchorX: toAnchorX(text.anchorX),
      anchorY: toAnchorY(text.anchorY),
      ...(outlined ? { halo: toHex(text.outlineColor ?? 0x000000) } : {}),
    });
  };

  labels.updateWorldMatrix(true, true);
  labels.traverseVisible((object) => {
    if (object instanceof TextBatch) {
      if (options.renderer && options.camera) {
        object.updateTexts(options.renderer, options.camera);
      }
      object.texts
        .filter((text) => text.visible)
        .forEach((text) => addText(text, object.getTextMatrixWorld(text)));
    } else if (object instanceof Text) {
      addText(object, object.matrixWorld);
    }
  });

  return texts;
}

/**
 * Describes what only SkyView knows for the backend to draw: line and area assets by their ids,
 * and the labels and POIs as vectors.
 *
 * @param assets The assets that are shown and in the view.
 * @param pois The POIs and clusters that are shown, anywhere.
 */
export function collectVectorLayers(
  assets: ElementObj[],
  pois: (Poi | Cluster)[],
  options: VectorLayerOptions,
): VectorLayers {
  const result: VectorLayers = {
    layers: [],
    files: {},
    layerIds: new Map(),
    paintedLast: [],
  };

  const addVectors = (id: string, name: string | undefined, content: VectorLayerContent) => {
    const file = `layers/${id}.json.gz`;
    result.layers.push({ id, name, kind: 'vectors', file, opacity: 1 });
    result.files[file] = content;
  };

  assets.forEach((asset, index) => {
    if (asset instanceof Geometry && asset.assetId) {
      const id = `geometry-${index + 1}`;
      result.layers.push({
        id,
        name: asset.name || undefined,
        kind: 'geometry',
        assetId: asset.assetId,
        opacity: asset.opacity,
        lineColor: options.blackLinesAndText ? BLACK : toHex(geometryStyle.lineColor),
        areaColor: toHex(geometryStyle.areaColor),
        areaOpacity: geometryStyle.areaOpacity,
        depthTest: asset.getDepthTest(),
      });
      result.layerIds.set(asset, id);
    }

    if (asset.labelsGroup?.visible) {
      const texts = collectTexts(asset.labelsGroup, options);
      if (texts.length > 0) {
        const id = `labels-${index + 1}`;
        addVectors(id, asset.name ? `${asset.name} labels` : undefined, {
          primitives: [],
          texts,
          pois: [],
        });
        result.layerIds.set(asset.labelsGroup, id);
      }
    }
  });

  const shownPois: VectorPoi[] = pois
    .filter((poi) => options.isInView(poi.position))
    .map((poi) =>
      poi instanceof Cluster
        ? {
            position: poi.position.clone().add(options.origo).toArray() as Position3,
            icon: 'cluster',
            count: poi.getClusterSize(),
          }
        : {
            position: poi.position.clone().add(options.origo).toArray() as Position3,
            label: poi.name || undefined,
            icon: poi.is360Poi() ? 'poi-360' : 'poi',
          },
    );
  if (shownPois.length > 0) {
    addVectors('pois', undefined, { primitives: [], texts: [], pois: shownPois });
    result.paintedLast.push('pois');
  }

  return result;
}
